import BrokenImage from '@mui/icons-material/BrokenImage';
import {Box, BoxProps, CircularProgress} from '@mui/material';
import {FC, useEffect, useRef} from 'react';
import {useGetConfigurationLayer} from '@src/api/configurations.ts';
import {useGetProcessImage} from '@src/api/process.ts';
import {HighlightMember} from '@src/types/configurations.ts';

export type VisualizationThumbnailProps = {
  rootProps?: BoxProps;
  highlight?: HighlightMember;
};

export const VisualizationThumbnail: FC<VisualizationThumbnailProps> = ({
  rootProps = {},
  highlight,
}) => {
  const height = 70;
  const width = 70;
  const {data: layer} = useGetConfigurationLayer(
    highlight?.instanceId,
    highlight?.layerId,
  );

  const {data, error, isLoading} = useGetProcessImage({
    evalscript: layer?.styles?.[0]?.evalScript,
    collectionId: layer?.collectionId,
    geometry: highlight?.areaOfInterest,
    from: highlight?.fromTime,
    to: highlight?.toTime,
    width: 256,
    height: 256,
  });

  const srcRef = useRef<{data: unknown; src: string | null}>({
    data: null,
    src: null,
  });

  if (data && srcRef.current.data !== data) {
    srcRef.current.data = data;
    srcRef.current.src = URL.createObjectURL(data);
  }

  useEffect(
    () => () => {
      if (srcRef.current.src) {
        srcRef.current.data = '';
        URL.revokeObjectURL(srcRef.current.src);
      }
    },
    [],
  );

  return error || isLoading ? (
    <Box
      sx={{
        width,
        height,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: error ? 'action.disabled' : undefined,
      }}
    >
      {error ? (
        <BrokenImage titleAccess="Image error" />
      ) : (
        <CircularProgress sx={{width, height}} />
      )}
    </Box>
  ) : (
    <Box {...rootProps}>
      <Box
        component="img"
        alt={highlight?.title}
        src={srcRef.current.src}
        width={width}
        height={height}
        sx={{
          aspectRatio: 1,
          backgroundColor: 'text.secondary',
          typography: 'caption',
          flexShrink: 0,
        }}
        {...rootProps}
      />
    </Box>
  );
};

export default VisualizationThumbnail;
